// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useAcceptProviderServiceRequest } from '@hooks/mutations/registry/onboarding/useAcceptProviderServiceRequest';
import useCredentials from '@hooks/queries/credential/useCredentials';
import useRegistryOperatorConfiguration from '@hooks/queries/registry/configuration/useRegistryOperatorConfiguration';
import useIsPackageAvailableFrom from '@hooks/queries/settings/useIsPackageAvailableFrom';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import {
  ProviderServiceRequest,
  ProviderServiceRequest_Accept,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Provider';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput, { TextInputLabel } from '../../../components/Form/TextInput';
import { useParties } from '../../../contexts/PartiesContext';
import { validateFulfillsAllRequirements } from '../../../utils/credential';
import { isPositiveFloat } from '../../../utils/isPositiveFloat';
import { ERR_ACC_REGISTRAR_SVC } from '../../../utils/strings';
import { Row } from './types';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  row: Row;
  disableDialog?: boolean;
};

export const AddWeightDialog = ({ disableDialog, row, open, onClose }: DialogProps) => {
  const [weight, setWeight] = useState<string>('');
  const [beneficiary, setBeneficiary] = useState('');
  const [showAmountError, setShowError] = React.useState(false);
  const operatorConfiguration = useRegistryOperatorConfiguration();

  const { party, dso, operator } = useParties();

  const accept = useAcceptProviderServiceRequest();
  const areConfigDetailsComplete = weight && beneficiary;
  const credentials = useCredentials();
  const enableAppRewardsConfig = useIsPackageAvailableFrom(
    'utility-registry-app-v0',
    [party, operator],
    '0.3.0',
  );
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowError(false);
    const w = e.target.value;
    if (isPositiveFloat(w)) {
      setWeight(w);
      const decimal = parseFloat(w);
      if (decimal > 1) {
        setShowError(true);
      }
    }
  };
  const onAccept = async () => {
    if (!operatorConfiguration.data) {
      return;
    }
    const { providerRequirements } = operatorConfiguration.data.payload;
    if (!credentials.data) {
      return;
    }
    const now = new Date(); // local time
    const utcNow = new Date(now.toUTCString()); // utc time
    const { fulfillsAllRequirements, fulfillingCredentials } = validateFulfillsAllRequirements(
      utcNow,
      row.user,
      providerRequirements,
      credentials.data,
    );
    if (!fulfillsAllRequirements) {
      enqueueSnackbar(`${ERR_ACC_REGISTRAR_SVC}: Credentials could not be found.`, {
        variant: 'error',
      });
    }
    const payload: ProviderServiceRequest_Accept = {
      operatorConfigurationCid: operatorConfiguration.data.contractId,
      credentialCids: fulfillingCredentials.map((c) => c.contractId),
      appRewardConfigurationDetails: areConfigDetailsComplete
        ? { dso, operatorAppRewardBeneficiary: { beneficiary, weight } }
        : null,
    };
    await accept({
      cid: row.contractId as ContractId<ProviderServiceRequest>,
      argument: payload,
    });
    onClose();
  };
  const disableSubmit = enableAppRewardsConfig
    ? !areConfigDetailsComplete || showAmountError
    : false;

  const resetState = useCallback(() => {
    setWeight('');
    setBeneficiary('');
    setShowError(false);
  }, [setWeight]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const { isLoading } = enableAppRewardsConfig;
  if (isLoading) return null;

  return (
    <FormDialog
      open={open}
      title="Provider Service"
      onCancel={handleClose}
      onSubmit={onAccept}
      submitName="Accept"
      disableDialog={disableDialog}
      isSubmitDisabled={disableSubmit}
    >
      {enableAppRewardsConfig.data && (
        <TextInput
          enableTopLabel
          label="Beneficiary"
          value={beneficiary}
          setValue={setBeneficiary}
        />
      )}
      {enableAppRewardsConfig.data && (
        <>
          <Box sx={{ mb: 1 }}>
            <TextInputLabel label="Weight" />
            <Typography variant="caption">
              Value must be greater than 0, and less than or equal to 1
            </Typography>
          </Box>
          <TextField
            placeholder="weight"
            variant="outlined"
            fullWidth
            size="small"
            value={weight}
            onChange={onChange}
          />
        </>
      )}
      {showAmountError && (
        <Typography variant="caption" color="error">
          Value is greater than 1. Reduce the weight.
        </Typography>
      )}
    </FormDialog>
  );
};
